import type { FactBox, Read } from "@/components/ui/box.schema";
import type { CourseContent as Content, ContentMode } from "@/lib/api/courseContent";
import type { VisitLevel } from "@/lib/visit/address";
import type { VisitResolution } from "@/lib/visit/registry";
import { ContentBody } from "./CourseContent";
import { contentRead, useCourseContent } from "./use-course-content";
const owner = { system: "imam" } as const;

export function contentBox(read: Read<Content>, topicId?: string): FactBox<Content> {
  return { id: "course-content", kind: "fact", label: "Course content", job: "Read the selected course content.", owner, read, fold: "never",
    detail: () => "Topics, readings, session guidance and coursework", body: value => <ContentBody value={value} topicId={topicId} /> };
}
export function useCoursePreview(level: VisitLevel | null): VisitResolution | null {
  const course = level?.id ?? "";
  const mode: ContentMode = level?.job === "published" ? "published-preview" : "draft-preview";
  const query = useCourseContent(course, mode, !!level);
  if (!level) return null;
  const read = contentRead(query);
  if (read.status === "forbidden") return { status: "refused" };
  if (read.status === "loading") return { status: "loading" };
  const scope: FactBox<Content> = { id: "preview-scope", kind: "fact", label: mode === "draft-preview" ? "Saved draft preview" : "Published view", job: "Read without acting as a learner.", owner, read, fold: "never",
    detail: () => "Generic learner", body: value => <div className="grid gap-2"><p>{mode === "draft-preview" ? "This shows saved preparation. Writing still in an editor is not included." : "This shows currently open whole-section content. Selected audiences and personal work are not included."}</p>
      {value.course_status !== "published" && <p>Learners cannot read this course yet. Published content waits for course publication.</p>}
      <p>This preview creates no attempt and publishes nothing.</p></div>,
    exits: [{ label: mode === "draft-preview" ? "Published view" : "Saved draft preview", visit: `course-preview:${course}/${mode === "draft-preview" ? "published" : "draft"}` }],
  };
  return { status: "ready", subject: "Learner preview", boxes: [scope, contentBox(read)], opens: [scope.id, "course-content"] };
}

export function useLearnerTopicContent(level: VisitLevel | null): VisitResolution | null {
  const query = useCourseContent(level?.id ?? "", "learner", !!level);
  if (!level) return null;
  const read = contentRead(query);
  if (read.status === "forbidden") return { status: "refused" };
  if (read.status === "loading") return { status: "loading" };
  const topic = query.data?.topics.find(row => row.id === level.job);
  if (query.data && !topic) return { status: "refused" };
  const box = contentBox(read, level.job ?? "");
  return { status: "ready", subject: topic?.title ?? "Topic", boxes: [box], opens: [box.id] };
}
